import React from 'react'
import MaterialTable, { Action, MaterialTableProps, MTableAction, Query } from 'material-table'
import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core'

import { tableIcons } from 'components/icons/tableIcons'
import { Search, Trash2, X } from 'lib/lucide'

interface CreateAction<T extends object> extends Action<T> {
  buttonText?: string
}

const useStyles = makeStyles({
  tooltip: {
    fontSize: '16px',
  },
  tableWrap: {
    maxWidth: '100%',
  },
  customToolbar: {
    padding: '16px 24px 12px',
  },
  toolbarTitle: {
    marginBottom: 8,
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  searchField: {
    minWidth: 280,
    maxWidth: 360,
    '& .MuiInput-underline': {
      paddingLeft: 0,
    },
    '& .MuiInputBase-root': {
      paddingLeft: 0,
    },
    '& .MuiInputBase-input': {
      paddingLeft: 0,
    },
  },
  searchIcon: {
    display: 'flex',
    color: '#1A171B',
    marginLeft: 4,
  },
  toolbarActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  createButton: {
    whiteSpace: 'nowrap',
    marginLeft: 8,
  },
  toolbarButton: {
    whiteSpace: 'nowrap',
    marginLeft: 8,
    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: 8,
    },
  },
})

const resolveAction = <T extends object>(action: unknown, data: T): CreateAction<T> | null => {
  let resolved = action
  if (typeof resolved === 'function') {
    resolved = (resolved as (row: T) => CreateAction<T> | null)(data)
  }
  if (resolved && typeof resolved === 'object' && 'action' in resolved) {
    const nested = (resolved as { action?: (row: T) => CreateAction<T> | null }).action
    if (typeof nested === 'function') {
      resolved = nested(data)
    }
  }
  if (!resolved || (resolved as CreateAction<T>).hidden) return null
  return resolved as CreateAction<T>
}

const TableAction = <T extends object>(props: {
  action: unknown
  data: T
  disabled?: boolean
  size?: string
}) => {
  const classes = useStyles()
  const action = resolveAction(props.action, props.data)
  if (!action) return null

  const isCreateButton = Boolean(action.isFreeAction && action.icon === 'add')
  if (isCreateButton) {
    return (
      <Button
        variant="contained"
        color="primary"
        disabled={action.disabled || props.disabled}
        className={classes.createButton}
        onClick={event => {
          action.onClick?.(event, props.data)
          event.stopPropagation()
        }}
      >
        {action.buttonText || action.tooltip || 'Nuevo'}
      </Button>
    )
  }

  if (action.isFreeAction && action.buttonText) {
    const startIcon =
      typeof action.icon === 'function' ? action.icon() : undefined
    return (
      <Button
        variant="outlined"
        disabled={action.disabled || props.disabled}
        className={classes.toolbarButton}
        startIcon={startIcon}
        onClick={event => {
          action.onClick?.(event, props.data)
          event.stopPropagation()
        }}
      >
        {action.buttonText}
      </Button>
    )
  }

  if (action.icon === 'delete') {
    const isDisabled = Boolean(action.disabled || props.disabled)
    const button = (
      <IconButton
        color="inherit"
        disabled={isDisabled}
        onClick={event => {
          action.onClick?.(event, props.data)
          event.stopPropagation()
        }}
      >
        <Trash2 size={20} fill="none" stroke="currentColor" />
      </IconButton>
    )

    if (!action.tooltip) return button

    return isDisabled ? (
      <Tooltip title={action.tooltip}>
        <span>{button}</span>
      </Tooltip>
    ) : (
      <Tooltip title={action.tooltip}>{button}</Tooltip>
    )
  }

  return <MTableAction {...(props as object)} />
}

interface TableToolbarProps {
  title?: React.ReactNode
  showTitle?: boolean
  search?: boolean
  searchText?: string
  onSearchChanged: (text: string) => void
  localization?: { searchPlaceholder?: string }
  actions?: Array<{ position?: string }>
  components: { Actions: React.ComponentType<{ actions: unknown; components: unknown }> }
}

const TableToolbar: React.FC<TableToolbarProps> = props => {
  const classes = useStyles()
  const title = props.showTitle ? props.title : null
  const toolbarActions = (props.actions || []).filter(action => action.position === 'toolbar')

  return (
    <div className={classes.customToolbar}>
      {title ? (
        <div className={classes.toolbarTitle}>
          {typeof title === 'string' ? (
            <Typography variant="h6">{title}</Typography>
          ) : (
            title
          )}
        </div>
      ) : null}
      <div className={classes.toolbarRow}>
        {props.search ? (
          <TextField
            className={classes.searchField}
            value={props.searchText || ''}
            onChange={event => props.onSearchChanged(event.target.value)}
            placeholder={props.localization?.searchPlaceholder || 'Buscar'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {props.searchText ? (
                    <IconButton size="small" onClick={() => props.onSearchChanged('')}>
                      <X size={16} />
                    </IconButton>
                  ) : null}
                  <span className={classes.searchIcon}>
                    <Search size={18} />
                  </span>
                </InputAdornment>
              ),
            }}
          />
        ) : null}
        {toolbarActions.length > 0 ? (
          <div className={classes.toolbarActions}>
            <props.components.Actions actions={toolbarActions} components={props.components} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

const buildPaginationQuery = <T extends object>(query: Query<T>) => {
  const { page, pageSize, search, orderBy, orderDirection } = query

  const pageQuery = `page=${page ?? 0}`
  const pageSizeQuery = `&pageSize=${pageSize ?? 10}`
  const searchQuery = search ? `&search=${search}` : ''
  const orderByQuery = orderBy ? `&orderBy=${String(orderBy.field)}` : ''
  const orderDirectionQuery = orderDirection ? `&orderDirection=${orderDirection}` : ''

  return `${pageQuery}${pageSizeQuery}${searchQuery}${orderByQuery}${orderDirectionQuery}`
}

const DefaultTable = <T extends object>(props: MaterialTableProps<T>) => {
  const classes = useStyles()
  const { options, columns, icons, components, ...restProps } = props

  const enhancedColumns = columns.map((column, index) => ({
    ...column,
    cellStyle: {
      maxWidth: '125px',
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden' as const,
      textOverflow: 'ellipsis' as const,
      padding: '8px',
      paddingLeft: index === 0 ? '24px' : undefined,
      ...column.cellStyle,
    },
    headerStyle: {
      paddingLeft: index === 0 ? '24px' : undefined,
      ...column.headerStyle,
    },
    render:
      column.render ??
      ((rowData: T) => {
        const cellValue = rowData[column.field as keyof T]
        return cellValue ? (
          <Tooltip
            title={String(cellValue)}
            placement="bottom-start"
            classes={{ tooltip: classes.tooltip }}
          >
            <div
              style={{
                whiteSpace: 'nowrap' as const,
                overflow: 'hidden' as const,
                textOverflow: 'ellipsis' as const,
              }}
            >
              {cellValue}
            </div>
          </Tooltip>
        ) : null
      }),
  }))

  return (
    <div className={classes.tableWrap}>
      <MaterialTable
        localization={{
          pagination: {
            labelDisplayedRows: '{from}-{to} de {count}',
            labelRowsSelect: 'filas',
          },
          toolbar: {
            nRowsSelected: '{0} filas(s) seleccionadas',
            searchTooltip: 'Buscar',
            searchPlaceholder: 'Buscar',
          },
          header: {
            actions: 'Acciones',
          },
          body: {
            emptyDataSourceMessage: 'No hay nada todavía.',
            filterRow: {
              filterTooltip: 'Filtrar',
            },
          },
        }}
        options={{
          pageSize: 10,
          pageSizeOptions: [5, 10, 20, 50, 100],
          actionsColumnIndex: columns.length,
          searchFieldStyle: {
            marginRight: '8px',
            paddingLeft: 0,
          },
          headerStyle: {
            padding: '16px',
          },
          rowStyle: (data, index) => ({
            backgroundColor: index % 2 === 0 ? '#FAFAFA' : '#FFF',
            height: 56,
          }),
          actionsCellStyle: {
            paddingRight: '8px',
          },
          ...options,
          searchFieldAlignment: 'left',
          toolbarButtonAlignment: 'right',
        }}
        icons={{ ...tableIcons, ...icons }}
        components={{
          ...components,
          Action: TableAction,
          Toolbar: TableToolbar,
        }}
        columns={enhancedColumns}
        {...restProps}
      />
    </div>
  )
}

export { DefaultTable, buildPaginationQuery }
